<template>
  <span v-if="trendMessage" class="flex text-14" :class="trendHigher ? 'higher' : 'lower'">
    <svg role="presentation" focusable="false" tabindex="-1" v-if="trendHigher" class="icon icon-sm"   alt="" svg-inline  fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17">
  <path fill="currentColor" fill-rule="evenodd" d="m8.5 3.7 5.2 9H3.3l5.2-9Z" clip-rule="evenodd"/>
</svg>
    <svg role="presentation" focusable="false" tabindex="-1" v-else class="icon icon-sm"   alt="" svg-inline  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16">
  <path fill="currentColor" fill-rule="evenodd" d="m8 12.7-5.2-9h10.4z"/>
</svg>
    <span class="font-normal text-gray-700" v-html="trendMessage" />
  </span>
</template>
<script lang="ts" setup>
import type { ChartData } from 'chart.js'
import type { Corridor } from '@core/types/imt'

const props = defineProps<{ midMarketRate: number; chartData: Record<string, ChartData> | null }>()

const { $lang, $t } = useNuxtApp()

const trendMessage = ref('')
const trendHigher = ref(false)
const corridor = inject<Corridor | undefined>('corridor', undefined)

onMounted(async () => {
  const lastComparisonInfo = await getLastComparisonInfo()

  if (!props.chartData) {
    return null
  }

  if (props.midMarketRate >= getHighest(7, props.chartData)) {
    // Find out for how many days it has been at its highest
    const reversed = [...props.chartData[60].datasets[0].data].reverse()
    let days = 0
    while (
      days < reversed.length &&
      props.midMarketRate >= Number(formatRate(reversed[days] as number))
    ) {
      days++
    }
    setTrendHighestText(days)
    return
  }

  // Since last visit
  if (
    corridor &&
    lastComparisonInfo?.midMarketRate &&
    lastComparisonInfo.countryFrom === corridor.countryFrom.iso &&
    lastComparisonInfo.countryTo === corridor.countryTo.iso &&
    lastComparisonInfo.currencyFrom === corridor.currencyFrom.iso &&
    lastComparisonInfo.currencyTo === corridor.currencyTo.iso
  ) {
    const daysSinceLastVisit = Math.round((Date.now() - lastComparisonInfo.timestamp) / 86400000)
    if (daysSinceLastVisit <= 30 && daysSinceLastVisit >= 2) {
      setTrendDeltaText(lastComparisonInfo.midMarketRate, true)
    }
  }

  if (!trendMessage.value) {
    const rates = props.chartData[60].datasets[0].data
    const rateYesterday = props.chartData[60].datasets[0].data[rates.length - 2] as number
    setTrendDeltaText(rateYesterday, false)
  }
})

function CalcPercentageDiff(rate: number) {
  return Number((100 * (props.midMarketRate / rate - 1)).toFixed(2))
}

function getHighest(range: number, chartData: Record<string, ChartData>): number {
  const data = [...chartData[range].datasets[0].data] as number[]
  return data.sort((a, b) => b - a)[0]
}

function setTrendHighestText(days: number) {
  trendHigher.value = true
  trendMessage.value = $t('exchange_rate_trend.day_high', { days })
}

function setTrendDeltaText(rate: number, lastVisit: boolean) {
  const percentageDiff = CalcPercentageDiff(rate)
  if (Math.abs(percentageDiff)) {
    const delta = formatNumber(Math.abs(percentageDiff), $lang, {
      maximumFractionDigits: 2,
    })
    if (percentageDiff > 0) {
      trendHigher.value = true
      trendMessage.value = $t(
        lastVisit
          ? 'exchange_rate_trend.percent_higher_last'
          : 'exchange_rate_trend.percent_higher_yesterday',
        {
          delta,
        },
      )
    } else {
      trendHigher.value = false
      trendMessage.value = $t(
        lastVisit
          ? 'exchange_rate_trend.percent_lower_last'
          : 'exchange_rate_trend.percent_lower_yesterday',
        {
          delta,
        },
      )
    }
  }
}
</script>
<style scoped>
.higher {
  @apply text-green-800;
}
.higher :deep(strong) {
  @apply text-green-800;
}
.lower {
  @apply text-red-900;
}
.lower :deep(strong) {
  @apply text-red-900 font-normal;
}
</style>
